import { Avatar as BaseAvatar } from "@base-ui/react/avatar";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import type React from "react";
import { type CSSProperties, forwardRef } from "react";
import {
  avatarFallbackClass,
  avatarImageClass,
  avatarRootClass,
  avatarSizeVar,
} from "./styles.css";

interface AvatarProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
  src?: string;
  alt?: string;
  fallback?: React.ReactNode;
  size?: number;
}

export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  { src, alt, fallback, size = 40, className, style, ...rest },
  ref,
) {
  const resolvedSize = `${size}px`;

  const initials = (() => {
    if (!alt) return "?";
    const words = alt.trim().split(/\s+/);
    const first = words[0]?.charAt(0) ?? "";
    const second = words[1]?.charAt(0) ?? "";
    return (first + second).toUpperCase() || "?";
  })();

  return (
    <BaseAvatar.Root
      ref={ref}
      {...rest}
      className={`${avatarRootClass}${className ? ` ${className}` : ""}`}
      style={{
        ...(assignInlineVars({
          [avatarSizeVar]: resolvedSize,
        }) as CSSProperties),
        ...style,
      }}
    >
      {src && (
        <BaseAvatar.Image src={src} alt={alt} className={avatarImageClass} />
      )}
      <BaseAvatar.Fallback className={avatarFallbackClass}>
        {fallback || initials}
      </BaseAvatar.Fallback>
    </BaseAvatar.Root>
  );
});

Avatar.displayName = "Avatar";
